import type { SupportCategory, SupportPlatform } from '@cottonray/support-contract'
import { accents, type AccentName, type ThemeName } from '@cottonray/design-tokens'
import { languageOf, type SupportLanguage } from './labels'

/**
 * 앱이 문의 화면을 열 때 주소로 넘겨 주는 것들.
 *
 * 화면이 앱에서 웹으로 옮겨 오면서(2026-08-26) 예전에 `SupportScreen` 의 props 로 넘기던
 * 값이 전부 **주소의 파라미터**가 됐다. 앱이 이 값을 채워 주므로 문의를 읽는 쪽이 보는
 * 정보는 그대로다 — 옮기면서 잃은 것이 없어야 한다는 것이 이전의 조건이었다.
 *
 * ## 개인정보는 쿼리스트링에 싣지 않는다
 *
 * 회신 주소는 **프래그먼트(`#`)로만** 받는다. 프래그먼트는 브라우저가 서버로 보내지 않아
 * nginx 접근 로그·Loki·리퍼러 어디에도 남지 않는다. 읽자마자
 * `history.replaceState` 로 주소에서 지우기까지 한다 — 그러면 주소창을 캡처해 문의에
 * 붙여도 메일 주소가 딸려 가지 않는다.
 *
 * 나머지(앱 버전·기기 모델·OS·언어)는 쿼리스트링이다. 그 값들은 어차피 문의 본문과 함께
 * 저장·표시되는 값이고, 링크를 손으로 만들어 확인할 수 있어야 한다.
 */
export interface SupportContext {
  /** 서버 검증과 같은 형식이어야 한다 — 아니면 400 이 온다 */
  service: string
  platform: SupportPlatform
  appVersion?: string
  osVersion?: string
  deviceModel?: string
  /** 기기 언어 태그. 문의에 실려 나간다 */
  locale?: string
  /**
   * 앱이 열어 준 것이 맞나. **방침 문구가 갈린다** — 브라우저에서 주소를 직접 연
   * 사람에게 "앱 버전·기기 모델이 함께 전달된다"고 적으면 거짓말이다.
   */
  fromApp: boolean
  /** 고를 수 있는 문의 종류. 광고가 없는 앱은 `ADS` 를 빼고 넘긴다 */
  categories: SupportCategory[]
  language: SupportLanguage
  /** 안 넘기면 시스템 설정(prefers-color-scheme)을 따른다 */
  theme?: ThemeName
  /** 디자인 토큰의 액센트 이름 — 앱과 **같은 값**이 나온다 */
  accent?: AccentName
  /** 토큰에 없는 색(고도 게임 등)일 때의 `#rrggbb` */
  accentHex?: string
  /** 프래그먼트로 받은 회신 주소 — 로그인한 앱이 채워 준다 */
  email?: string
  /**
   * 앱 안 웹뷰에 담겨 있나 (2026-08-26 사용자 지시: "주소표시 안되는 내장웹뷰. 마치 앱처럼").
   *
   * 켜지면 **페이지가 자기 제목을 안 그린다** — 그 자리는 앱 상단바가 이미 차지하고 있어
   * "지원 문의"가 두 번 나온다. 다 보내고 나서의 "이 창은 닫으셔도 돼요"도 뺀다: 닫을 창이
   * 없고, 돌아가는 길은 앱의 뒤로가기다.
   */
  embed: boolean
}

/** 기본 서비스. 주소를 손으로 열어 본 사람의 문의도 버리지 않는다 */
const DEFAULT_SERVICE = 'cottonray'

/** 서버(`SubmitInquiryRequest.service`)의 `@Pattern` 과 같은 값 */
const SERVICE_SHAPE = /^[a-z0-9][a-z0-9-]*$/

const PLATFORMS: Record<string, SupportPlatform> = {
  android: 'ANDROID',
  ios: 'IOS',
  web: 'WEB',
  desktop: 'DESKTOP',
}

const CATEGORIES: Record<string, SupportCategory> = {
  bug: 'BUG',
  idea: 'IDEA',
  account: 'ACCOUNT',
  ads: 'ADS',
  etc: 'ETC',
}

/** 앱이 안 넘겼을 때. `ADS` 는 광고를 붙인 앱만 넘긴다 — 없는 것을 고르게 하지 않는다 */
const DEFAULT_CATEGORIES: SupportCategory[] = ['BUG', 'IDEA', 'ACCOUNT', 'ETC']

const HEX_SHAPE = /^#?([0-9a-f]{6})$/i

/**
 * 주소를 읽는다. **아무것도 안 넘어와도 화면은 뜬다** — 잘못된 값은 무시하고 기본값으로
 * 내려간다. 파라미터 하나가 틀렸다고 문의를 못 보내게 만들면, 그 사람은 문의할 방법이
 * 남지 않는다.
 */
export function readContext(search: string, hash: string, navigatorLanguage?: string): SupportContext {
  const query = new URLSearchParams(search)
  const fragment = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash)

  const service = text(query.get('service'), 40)
  const locale = text(query.get('locale'), 16)
  const platform = PLATFORMS[query.get('platform')?.trim().toLowerCase() ?? '']

  return {
    service: service !== undefined && SERVICE_SHAPE.test(service) ? service : DEFAULT_SERVICE,
    // 앱이 안 밝히면 웹이다 — 실제로 브라우저에서 열린 것이 맞다
    platform: platform ?? 'WEB',
    ...optional('appVersion', text(query.get('appVersion'), 32)),
    ...optional('osVersion', text(query.get('osVersion'), 48)),
    ...optional('deviceModel', text(query.get('device'), 64)),
    ...optional('locale', locale),
    fromApp: platform !== undefined && platform !== 'WEB',
    categories: readCategories(query.get('topics')),
    // 페이지 언어는 앱이 고른 것이 우선 — 앱 UI 가 일본어인데 문의만 한국어로 뜨면 안 된다.
    // 없으면 기기 언어, 그것도 없으면 브라우저 설정 순이다
    language: languageOf(query.get('lang') ?? locale ?? navigatorLanguage),
    ...readTheme(query.get('theme'), query.get('accent')),
    ...optional('email', text(fragment.get('email'), 254)),
    embed: query.get('embed') === '1',
  }
}

function readCategories(raw: string | null): SupportCategory[] {
  const parsed = (raw ?? '')
    .split(',')
    .map((part) => CATEGORIES[part.trim().toLowerCase()])
    .filter((category): category is SupportCategory => category !== undefined)
  // 중복은 눌러 준다 — 칩이 두 번 그려지면 고른 것이 어느 쪽인지 알 수 없다
  const unique = [...new Set(parsed)]
  return unique.length > 0 ? unique : DEFAULT_CATEGORIES
}

/**
 * 테마. **이름으로 받는 쪽이 정확하다** — 앱이 `ThemeProvider accent="rose"` 를 쓰면
 * 웹도 같은 토큰을 읽어 값이 한 곳에서 나온다. 색을 직접 넘기면 토큰이 바뀔 때 둘이 어긋난다.
 *
 * 그래도 `#rrggbb` 를 받는 이유는 토큰을 안 쓰는 것들(고도 게임·외부 링크) 때문이다.
 */
function readTheme(
  theme: string | null,
  accent: string | null,
): Pick<SupportContext, 'theme' | 'accent' | 'accentHex'> {
  const name = theme?.trim().toLowerCase()
  const scheme: ThemeName | undefined = name === 'dark' || name === 'light' ? name : undefined

  const raw = accent?.trim() ?? ''
  if (raw in accents) {
    return { ...optional('theme', scheme), accent: raw as AccentName }
  }
  const digits = HEX_SHAPE.exec(raw)?.[1]
  if (digits !== undefined) {
    return { ...optional('theme', scheme), accentHex: `#${digits.toLowerCase()}` }
  }
  return optional('theme', scheme)
}

/**
 * `exactOptionalPropertyTypes` 아래서는 `{ x: undefined }` 가 `{ x?: string }` 에 안 들어간다 —
 * 값이 없으면 **키 자체를 만들지 않는다**.
 */
function optional<K extends string, V>(key: K, value: V | undefined): { [P in K]?: V } {
  return (value === undefined ? {} : { [key]: value }) as { [P in K]?: V }
}

function text(raw: string | null, max: number): string | undefined {
  const trimmed = raw?.trim()
  if (trimmed === undefined || trimmed === '') return undefined
  // 서버의 @Size 와 같은 상한에서 자른다 — 넘겨서 400 을 받느니 잘라서 접수되는 편이 낫다
  return trimmed.slice(0, max)
}

/**
 * 액센트 색 하나로 CSS 변수 넷을 만든다 (`ui-web` 의 `ThemeProvider` 가 토큰 이름으로 하는 일).
 *
 * **글자색은 넘겨받지 않고 계산한다** — 앱이 색만 주는데 그 위 글자를 흰색으로 고정하면
 * 밝은 액센트에서 글자가 사라진다. WCAG 상대 휘도로 흑/백을 고른다.
 */
export function accentOverride(hex: string): string {
  const textOnAccent = luminance(hex) > 0.45 ? '#111318' : '#ffffff'
  return `:root, :root[data-theme="dark"], :root[data-theme="light"] {
--st-color-accent: ${hex};
--st-color-accent-hover: ${hex};
--st-color-accent-surface: color-mix(in srgb, ${hex} 14%, transparent);
--st-color-text-on-accent: ${textOnAccent};
}`
}

function luminance(hex: string): number {
  const channels = [1, 3, 5].map((start) => Number.parseInt(hex.slice(start, start + 2), 16) / 255)
  const [r, g, b] = channels.map((value) =>
    value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4,
  )
  return 0.2126 * (r ?? 0) + 0.7152 * (g ?? 0) + 0.0722 * (b ?? 0)
}

/**
 * 이 페이지의 문맥. **한 번만 읽는다** — 프래그먼트는 화면이 뜨자마자 주소에서 지워지므로
 * (SupportInquiry 의 `replaceState`) 두 번째 호출은 회신 주소를 못 본다. 주소는 이 페이지가
 * 사는 동안 바뀌지 않으니 캐시가 곧 정답이다.
 */
let cached: SupportContext | undefined

export function supportContext(): SupportContext {
  cached ??= readContext(window.location.search, window.location.hash, navigator.language)
  return cached
}
